import React, { useMemo } from 'react';
import { Box, HStack, VStack, Text, Image } from '@chakra-ui/react';
import { FiClock, FiCalendar } from 'react-icons/fi';
import { useTranslation } from '../../hooks/useTranslation';
import { Booking } from '../../api/rooms/types';
import { format } from 'date-fns';
import { modalManager } from '../../utils/modalManager';
import { useGetCustomerQuery } from '../../api';
import { useNavigate } from 'react-router-dom';
import { formatTime, formatDuration } from '../../utils/dateUtils';

interface BookingCardProps {
    booking: Booking;
}

const BookingCard: React.FC<BookingCardProps> = ({ booking }) => {
    const { t, dateFnsLocale } = useTranslation();
    const { data: customer } = useGetCustomerQuery();
    const navigate = useNavigate();

    // Форматируем дату
    const formattedDate = useMemo(() => {
        try {
            const date = new Date(booking.booking_date);
            return format(date, 'd MMMM yyyy', { locale: dateFnsLocale });
        } catch {
            return booking.booking_date;
        }
    }, [booking.booking_date, dateFnsLocale]);

    // Вычисляем продолжительность
    const duration = useMemo(() => {
        return formatDuration(booking.start_time, booking.end_time, {
            hours: t.pages.office.hours,
            minutes: t.pages.office.minutes,
        });
    }, [booking.start_time, booking.end_time, t]);

    const handleCardClick = () => {
        if (!customer?.personal_code) {
            return;
        }

        const handleShowQrCode = () => {
            modalManager.hide();
            navigate('/office/access');
        };

        modalManager.show({
            title: t.pages.office.bookingQrModalTitle,
            message: t.pages.office.bookingQrModalMessage,
            size: 'md',
            maxWidth: '400px',
            buttons: [
                {
                    type: 'secondary',
                    text: t.common.close,
                    onClick: () => {
                        modalManager.hide();
                    },
                },
                {
                    type: 'success',
                    text: t.pages.office.showQrCode,
                    onClick: handleShowQrCode,
                },
            ],
        });
    };

    return (
        <Box
            bg="white"
            p={2}
            cursor="pointer"
            _hover={{
                boxShadow: 'md',
                transform: 'translateY(-2px)',
            }}
            transition="all 0.2s"
            onClick={handleCardClick}
        >
            <HStack gap={4} align="center">
                {/* Картинка */}
                <Box w="120px" h="80px" bg="gray.200" position="relative" overflow="hidden" flexShrink={0}>
                    <Image
                        src={booking.room_image_links[0]}
                        alt={booking.room_name}
                        w="full"
                        h="full"
                        objectFit="cover"
                    />
                </Box>

                {/* Информация */}
                <VStack align="flex-start" gap={2} flex={1}>
                    <Text fontSize="lg" fontWeight="bold" color="black">
                        {booking.room_name}
                    </Text>

                    {/* Дата */}
                    <HStack gap={2} align="center">
                        <FiCalendar size={16} color="gray" />
                        <Text fontSize="sm" color="gray.600">
                            {formattedDate}
                        </Text>
                    </HStack>

                    {/* Время и продолжительность */}
                    <HStack gap={2} align="center">
                        <FiClock size={16} color="gray" />
                        <Text fontSize="sm" color="gray.600">
                            {formatTime(booking.start_time)} - {formatTime(booking.end_time)} ({duration})
                        </Text>
                    </HStack>
                </VStack>
            </HStack>
        </Box>
    );
};

export default BookingCard;
